<script>
import Datatable from "@/router/layouts/table-datatable.vue";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import axios from "axios";

export default {
  components: {
    Datatable,
  },
  props: {
    apiUrl: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    tabTitle: {
      type: String,
      required: true,
    },
    tabSubtitle: {
      type: String,
      required: true,
    },
    excelFileName: {
      type: String,
      required: true,
    },
    excelSheetName: {
      type: String,
      required: true,
    },
    summaryMainLabel: {
      type: String,
      required: true,
    },
    useStatusFilter: {
      type: Boolean,
      default: false,
    },
    useBaFilter: {
      type: Boolean,
      default: false,
    },
    requireBaForCalculation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingTable: true,
      all_rows: [],
      table_data: [],
      sortKey: "id",
      sortOrders: this.buildSortOrders(),
      tableFilter: {
        page: 1,
        per_page: 10,
        search: "",
        column: "id",
        dir: "desc",
        filter: {
          status: "",
          pengisian_ba: "",
        },
      },
      DPP_FACTOR: 0.5,
      HONOR_PER_PAKET: 500000,
    };
  },
  computed: {
    activeColumnCount() {
      return this.columns.length;
    },
    resolvedApiUrl() {
      if (/^https?:\/\//i.test(this.apiUrl)) {
        return this.apiUrl;
      }

      return process.env.VUE_APP_BACKEND_URL_VERSION + this.apiUrl;
    },
    filteredRows() {
      let rows = this.all_rows.slice();
      const search = String(this.tableFilter.search || "").trim().toLowerCase();

      if (search) {
        rows = rows.filter((row) =>
          [row.nama, row.nip, row.kompetensi, row.dahan_profesi]
            .map((value) => String(value || "").toLowerCase())
            .some((value) => value.includes(search)),
        );
      }

      if (this.useStatusFilter && this.tableFilter.filter.status) {
        rows = rows.filter((row) => row.status === this.tableFilter.filter.status);
      }

      if (this.useBaFilter && this.tableFilter.filter.pengisian_ba) {
        rows = rows.filter((row) => row.pengisian_ba === this.tableFilter.filter.pengisian_ba);
      }

      return rows;
    },
    summaryTotals() {
      const rows = this.filteredRows;
      const totalPaket = rows.reduce((sum, row) => sum + this.safeNumber(row.jumlah_paket), 0);
      const totalBruto = rows.reduce((sum, row) => sum + this.safeNumber(row.bruto), 0);
      const totalDpp = rows.reduce((sum, row) => sum + this.safeNumber(row.dpp), 0);
      const totalPph = rows.reduce((sum, row) => sum + this.safeNumber(row.pph), 0);
      const totalPaid = rows.reduce((sum, row) => sum + this.safeNumber(row.dibayarkan), 0);

      return {
        rowCount: rows.length,
        totalPaket,
        totalBruto,
        totalDpp,
        totalPph,
        totalPaid,
      };
    },
  },
  async mounted() {
    await this.getDataTable();
  },
  methods: {
    buildSortOrders() {
      const sortOrders = [];
      this.columns.forEach((column) => {
        sortOrders[column.name] = -1;
      });
      return sortOrders;
    },
    getDataTable(url = this.resolvedApiUrl) {
      this.loadingTable = true;
      this.tableFilter.page = 1;

      const config = {
        method: "get",
        url,
        params: this.tableFilter,
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + localStorage.access_token,
        },
      };

      return axios(config)
        .then((response) => {
          const responseData = response.data;
          const responseDataFix = responseData.data;
          if (responseData.meta.code == 200) {
            this.all_rows = responseDataFix.data;
            this.calculateAllRows();
            this.refreshTable();
          } else {
            Swal.fire({ icon: "error", title: "Oops...", text: responseData.meta.message });
          }
        })
        .catch((errors) => {
          console.log(errors);
        })
        .finally(() => {
          this.loadingTable = false;
        });
    },
    calculateAllRows() {
      this.all_rows.forEach((row) => {
        this.applyRowCalculations(row);
      });
    },
    applyRowCalculations(row) {
      const realisasi = this.safeNumber(row.realisasi_paket);
      const jumlahPaket = this.safeNumber(row.jumlah_paket);
      row.realisasi_paket = realisasi < 0 ? 0 : realisasi > jumlahPaket ? jumlahPaket : realisasi;
      row.honor_per_paket = this.HONOR_PER_PAKET;

      if (this.requireBaForCalculation && row.pengisian_ba !== "SUDAH") {
        row.jumlah_honor = 0;
        row.bruto = 0;
        row.dpp = 0;
        row.pph = 0;
        row.dibayarkan = 0;
        return;
      }

      row.jumlah_honor = row.honor_per_paket * row.realisasi_paket;
      row.bruto = row.jumlah_honor;
      row.dpp = this.safeNumber(row.bruto) * this.DPP_FACTOR;
      row.pph = row.dpp * 0.05;
      row.dibayarkan = row.bruto - row.pph;
    },
    safeNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    formatRupiah(value) {
      const number = this.safeNumber(value);
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(number);
    },
    refreshTable() {
      this.table_data = this.sortRows(this.filteredRows);
    },
    sortRows(rows) {
      if (!this.sortKey || this.sortKey === "-") {
        return rows;
      }

      const direction = this.sortOrders[this.sortKey] || 1;
      return rows.slice().sort((a, b) => {
        const valueA = a[this.sortKey];
        const valueB = b[this.sortKey];

        if (typeof valueA === "number" && typeof valueB === "number") {
          return (valueA - valueB) * direction;
        }

        return String(valueA || "").localeCompare(String(valueB || "")) * direction;
      });
    },
    sortBy(key) {
      if (key === "-") {
        return;
      }

      this.sortKey = key;
      this.sortOrders[key] = this.sortOrders[key] * -1;
      this.tableFilter.column = key;
      this.tableFilter.dir = this.sortOrders[key] === 1 ? "asc" : "desc";
      this.refreshTable();
    },
    onFilterChange() {
      this.refreshTable();
    },
    updateRowValue(row, field, value) {
      if (field !== "realisasi_paket") {
        return;
      }

      const realisasi = this.safeNumber(value);
      const jumlahPaket = this.safeNumber(row.jumlah_paket);
      if (realisasi < 0) {
        Swal.fire({ icon: "warning", title: "Nilai tidak valid", text: "Realisasi Paket tidak boleh bernilai negatif." });
        row.realisasi_paket = 0;
      } else if (realisasi > jumlahPaket) {
        Swal.fire({ icon: "warning", title: "Nilai melebihi jumlah paket", text: "Realisasi Paket tidak boleh lebih besar dari Jumlah Paket." });
        row.realisasi_paket = jumlahPaket;
      } else {
        row.realisasi_paket = realisasi;
      }

      this.applyRowCalculations(row);
      this.refreshTable();
    },
    getStatusBadgeClass(status) {
      if (status === "APPROVED") return "badge bg-success";
      if (status === "PENDING" || status === "DRAFT" || status === "REVISI") return "badge bg-warning text-dark";
      if (status === "REJECTED") return "badge bg-danger";
      return "badge bg-secondary";
    },
    getBaBadgeClass(value) {
      if (value === "memenuhi") return "badge bg-success";
      if (value === "tidak memenuhi") return "badge bg-secondary";
      if (value === "SEBAGIAN") return "badge bg-info text-dark";
      return "badge bg-secondary";
    },
    exportExcel() {
      const filtered = this.filteredRows.map((row, index) => this.mapRowForExport(row, index));

      if (filtered.length) {
        filtered.push(this.buildExportTotalRow());
      }

      try {
        const worksheet = XLSX.utils.json_to_sheet(filtered);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, this.excelSheetName);
        XLSX.writeFile(workbook, this.excelFileName);
      } catch (error) {
        this.exportCsv(filtered, this.excelFileName.replace(".xlsx", ".csv"));
      }
    },
    mapRowForExport(row, index) {
      return this.columns.reduce((exportRow, column) => {
        exportRow[column.label] = this.getExportValue(row, column, index);
        return exportRow;
      }, {});
    },
    buildExportTotalRow() {
      return this.columns.reduce((exportRow, column) => {
        exportRow[column.label] = this.getExportTotalValue(column);
        return exportRow;
      }, {});
    },
    getExportValue(row, column, index) {
      if (column.name === "id") return index + 1;
      return row[column.name];
    },
    getExportTotalValue(column) {
      const totals = {
        id: "TOTAL",
        jumlah_paket: this.summaryTotals.totalPaket,
        jumlah_honor: this.summaryTotals.totalBruto,
        bruto: this.summaryTotals.totalBruto,
        dpp: this.summaryTotals.totalDpp,
        pph: this.summaryTotals.totalPph,
        dibayarkan: this.summaryTotals.totalPaid,
      };

      return Object.prototype.hasOwnProperty.call(totals, column.name) ? totals[column.name] : "";
    },
    exportCsv(rows, fileName) {
      const headers = Object.keys(rows[0] || {});
      const csv = [headers.join(",")].concat(
        rows.map((row) =>
          headers
            .map((key) => {
              const value = row[key] === null || row[key] === undefined ? "" : String(row[key]);
              return `"${value.replace(/"/g, '""')}"`;
            })
            .join(","),
        ),
      );

      const blob = new Blob([csv.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<template>
  <div class="honor-calculation-tab">
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
      <div>
        <h5 class="fw-semibold mb-1">{{ tabTitle }}</h5>
        <div class="text-muted">{{ tabSubtitle }}</div>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <button class="btn btn-light btn-sm honor-action-btn" @click="getDataTable()">
          <i class="fa fa-sync-alt me-1"></i> Refresh
        </button>
        <button class="btn btn-success btn-sm honor-action-btn" @click="exportExcel">
          <i class="fa fa-file-excel me-1"></i> Export Excel
        </button>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-sm-6 col-xl">
        <div class="card honor-summary-card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">{{ summaryMainLabel }}</div>
            <div class="h4 fw-semibold mt-2 mb-0">{{ summaryTotals.rowCount }}</div>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl">
        <div class="card honor-summary-card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">Total Paket</div>
            <div class="h4 fw-semibold mt-2 mb-0">{{ summaryTotals.totalPaket }}</div>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl">
        <div class="card honor-summary-card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">Total Bruto</div>
            <div class="h5 fw-semibold mt-2 mb-0">{{ formatRupiah(summaryTotals.totalBruto) }}</div>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl">
        <div class="card honor-summary-card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">Total PPh</div>
            <div class="h5 fw-semibold mt-2 mb-0">{{ formatRupiah(summaryTotals.totalPph) }}</div>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl">
        <div class="card honor-summary-card shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">Total Dibayarkan</div>
            <div class="h5 fw-semibold mt-2 mb-0">{{ formatRupiah(summaryTotals.totalPaid) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
              <div class="d-flex flex-wrap align-items-end gap-2 honor-filter-area">
                <div class="honor-search-field">
                  <label class="form-label small text-muted mb-1">Search</label>
                  <input
                    v-model="tableFilter.search"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Cari nama, NIP, kompetensi, atau dahan profesi..."
                    @input="onFilterChange"
                  />
                </div>
                <div v-if="useStatusFilter" class="honor-filter-field">
                  <label class="form-label small text-muted mb-1">Status</label>
                  <select class="form-select form-select-sm" v-model="tableFilter.filter.status" @change="onFilterChange">
                    <option value="">Semua</option>
                    <option value="APPROVED">APPROVED</option>
                    <option value="DRAFT">DRAFT</option>
                    <option value="PENDING">PENDING</option>
                    <option value="REVISI">REVISI</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>
                </div>
                <div v-if="useBaFilter" class="honor-filter-field">
                  <label class="form-label small text-muted mb-1">Pengisian BA</label>
                  <select class="form-select form-select-sm" v-model="tableFilter.filter.pengisian_ba" @change="onFilterChange">
                    <option value="">Semua</option>
                    <option value="memenuhi">Memenuhi</option>
                    <option value="tidak memenuhi">Tidak Memenuhi</option>
                  </select>
                </div>
              </div>
              <!-- <div class="text-muted small">{{ table_data.length }} data ditampilkan</div> -->
            </div>

            <div class="row mb-3">
              <div class="col-12">
                <datatable
                  :columns="columns"
                  :sort-key="sortKey"
                  :sort-orders="sortOrders"
                  class-table="table table-sm table-bordered table-striped table-hover align-middle mb-0 small honor-table"
                  @sort="sortBy"
                >
                  <tbody>
                    <tr v-if="loadingTable">
                      <td class="text-center py-4 text-muted" :colspan="activeColumnCount">
                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Memuat data...
                      </td>
                    </tr>
                    <tr v-else-if="table_data.length === 0">
                      <td class="text-center py-4 text-muted" :colspan="activeColumnCount">
                        Tidak ada data honor yang ditemukan.
                      </td>
                    </tr>
                    <tr v-else v-for="(row, index) in table_data" :key="row.id">
                      <td class="text-center text-nowrap">{{ index + 1 }}</td>
                      <td>{{ row.dahan_profesi }}</td>
                      <td>{{ row.kompetensi }}</td>
                      <td>{{ row.nama }}</td>
                      <td class="text-nowrap">{{ row.nip }}</td>
                      <td class="text-end text-nowrap">{{ row.jumlah_paket }}</td>
                      <td v-if="useStatusFilter" class="text-center text-nowrap">
                        <span :class="getStatusBadgeClass(row.status)">{{ row.status }}</span>
                      </td>
                      <td v-if="useBaFilter" class="text-center text-nowrap">
                        <span :class="getBaBadgeClass(row.pengisian_ba)">{{ row.pengisian_ba }}</span>
                      </td>
                      <td class="text-end text-nowrap">{{ formatRupiah(row.honor_per_paket) }}</td>
                      <td class="text-end text-nowrap">
                        <input
                          v-model.number="row.realisasi_paket"
                          type="number"
                          class="form-control form-control-sm text-end honor-realisasi-input"
                          min="0"
                          @change="updateRowValue(row, 'realisasi_paket', row.realisasi_paket)"
                        />
                      </td>
                      <td class="text-end text-nowrap">{{ formatRupiah(row.jumlah_honor) }}</td>
                      <td class="text-end text-nowrap">{{ formatRupiah(row.bruto) }}</td>
                      <td class="text-end text-nowrap">{{ formatRupiah(row.dpp) }}</td>
                      <td class="text-end text-nowrap">{{ formatRupiah(row.pph) }}</td>
                      <td class="text-end text-nowrap">{{ formatRupiah(row.dibayarkan) }}</td>
                    </tr>
                  </tbody>
                </datatable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.honor-summary-card {
  border-color: #edf0f2;
}

.honor-summary-card .h4,
.honor-summary-card .h5 {
  color: #1f2937;
}

.honor-action-btn {
  min-width: 108px;
}

.honor-filter-area {
  flex: 1 1 auto;
}

.honor-search-field {
  min-width: 280px;
  flex: 1 1 320px;
}

.honor-filter-field {
  min-width: 170px;
}

.honor-realisasi-input {
  display: inline-block;
  max-width: 110px;
}

.honor-calculation-tab :deep(.honor-table thead) {
  background-color: #f8f9fa !important;
  color: #495057 !important;
}

.honor-calculation-tab :deep(.honor-table thead th) {
  border-bottom-color: #dee2e6;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.honor-calculation-tab :deep(.honor-table .badge) {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35em 0.55em;
}

@media (max-width: 575.98px) {
  .honor-action-btn,
  .honor-search-field,
  .honor-filter-field {
    width: 100%;
  }
}
</style>
